import {computed, type ComputedRef} from 'vue'
import {useAuthStore} from '@/stores/auth'
import {useRolesStore} from '@/stores/roles'

// ============================================================================
//  L2 IDENTITY (TEMPORARY HARDCODE)
// ============================================================================
//
// Проверяет, есть ли у текущего юзера роль с именем `l2_support`.
//
// Как работает:
//   1. authStore.user.roleUuids — UUID-ы моих ролей (приходят из MeQuery)
//   2. rolesStore.roles — все роли системы с {uuid, name} (грузятся в init batch)
//   3. Сопоставляем UUID-ы → имя; ищем 'l2_support'.
//
// Требования к бэку:
//   - Роль `l2_support` должна иметь permission `role|read`, иначе для L2 юзера
//     запрос `roles` в init batch упадёт и rolesStore.roles будет пустым.
//
// Используется в:
//   - VAsideMenu      — если isL2 → рендерим только L2-меню
//   - router/index.ts — если isL2 → блокируем все не-L2 маршруты
//   - router/guestGuard.ts — после логина редирект на /l2/organizations
//
// КОГДА УДАЛЯТЬ:
//   Когда L2-разделы переедут в динамический конфигуратор (catalogmenu в БД,
//   permissions per view). Тогда:
//     1. Удалить этот файл (useL2Identity.ts)
//     2. Удалить ветку `if (isL2.value) ...` в VAsideMenu.vue
//     3. Удалить ветку `if (isL2User()) ...` в router/index.ts beforeResolve
//     4. Удалить ветку `if (isL2User()) ...` в router/guestGuard.ts
//     5. Удалить src/constants/l2Menu.ts (если меню переезжает в БД)
//
//   Поиск по проекту `useL2Identity` / `isL2User` найдёт все 3-4 точки удаления.
// ============================================================================

const L2_ROLE_NAME = 'l2_support'

export interface L2Identity {
  isL2: ComputedRef<boolean>
}

function checkIsL2(
  roleUuids: string[] | undefined | null,
  findRole: (uuid: string) => {name: string} | null,
): boolean {
  // Partial-response от MeQuery / batchData.MeQuery === undefined приводит к тому,
  // что authStore.user.roleUuids может быть undefined несмотря на TS-тип. Не крашим
  // рендер меню — просто считаем, что юзер не L2.
  if (!Array.isArray(roleUuids)) return false
  return roleUuids.some(uuid => findRole(uuid)?.name === L2_ROLE_NAME)
}

export function useL2Identity(): L2Identity {
  const authStore = useAuthStore()
  const rolesStore = useRolesStore()
  const isL2 = computed(() => checkIsL2(
    authStore.user.roleUuids,
    rolesStore.getRoleByUuid,
  ))
  return {isL2}
}

// Для использования вне setup-контекста (router guards, прочая логика).
export function isL2User(): boolean {
  const authStore = useAuthStore()
  const rolesStore = useRolesStore()
  return checkIsL2(authStore.user.roleUuids, rolesStore.getRoleByUuid)
}
